import type { FunctionComponent } from 'react';
import { useCallback } from 'react';
import { FormattedMessage } from 'react-intl';

import { Analytics } from '@trello/atlassian-analytics';
import { useMemberId } from '@trello/authentication';
import { siteDomain } from '@trello/config';
import { localizeCount } from '@trello/legacy-i18n';
import { asCompactNumber, asNumber } from '@trello/legacy-i18n/formatters';
import { Button } from '@trello/nachos/button';
import { CopyIcon } from '@trello/nachos/icons/copy';
import { ShareIcon } from '@trello/nachos/icons/share';
import { SubscribeIcon } from '@trello/nachos/icons/subscribe';
import { Popover, usePopover } from '@trello/nachos/popover';
import { token } from '@trello/theme';
import { makeTemplateUrlSlug } from '@trello/urls';

import { LazyShareTemplatePopover } from 'app/src/components/ShareTemplatePopover/LazyShareTemplatePopover';
import { useBoardStatsQuery } from './BoardStatsQuery.generated';

import styles from './BoardMenuViewsAndCopies.less';

export const ViewsAndCopies: FunctionComponent<{
  isTemplate: boolean;
  showLabels: boolean;
  useSmallTextStyle: boolean;
  copyCount: number;
  viewCount: number;
}> = ({ isTemplate, showLabels, useSmallTextStyle, copyCount, viewCount }) => {
  const shouldShowViewCopy = isTemplate && copyCount >= 0 && viewCount > 0;

  const textStyle = useSmallTextStyle
    ? styles.viewCopyTextSmall
    : styles.viewCopyText;

  return (
    <div>
      {shouldShowViewCopy && (
        <div>
          <span
            className={textStyle}
            title={localizeCount('copies', asNumber(copyCount))}
          >
            <CopyIcon
              size="small"
              color={token('color.icon.subtle', '#626F86')}
              dangerous_className={styles.iconViewCopy}
            />
            {showLabels
              ? localizeCount('copies', asCompactNumber(copyCount))
              : asCompactNumber(copyCount)}
          </span>
          &nbsp;
          <span
            className={textStyle}
            title={localizeCount('views', asNumber(viewCount))}
          >
            <SubscribeIcon
              size="small"
              color={token('color.icon.subtle', '#626F86')}
              dangerous_className={styles.iconViewCopy}
            />
            {showLabels
              ? localizeCount('views', asCompactNumber(viewCount))
              : asCompactNumber(viewCount)}
          </span>
        </div>
      )}
    </div>
  );
};

export const BoardMenuViewsAndCopies: FunctionComponent<{
  idBoard: string;
  isTemplate: boolean;
  showLabels: boolean;
}> = ({ idBoard, isTemplate, showLabels }) => {
  const memberId = useMemberId();
  const { data, error, loading } = useBoardStatsQuery({
    /**
     * On public templates in private workspaces a user isn't a member of,
     * the CurrentBoardFull response does not include an organization field,
     * but some query made in-between introduces into the Apollo cache a board
     * organization object with just the id present. This condition causes Apollo
     * to make this API request (instead of reading from the cache) to try to
     * retrieve the missing organization displayName field that the query asks for
     *
     * Setting an explicit fetchPolicy forces Apollo to read from cache, and
     * returnPartialData allows it to return cached data even while the organization
     * object is non-null but missing a displayName
     */
    fetchPolicy: 'cache-only',
    returnPartialData: true,
    variables: { boardId: idBoard, memberId },
    waitOn: ['CurrentBoardInfo', 'MemberHeader'],
  });

  const member = data?.member;
  const board = data?.board;

  const { triggerRef, popoverProps, toggle } = usePopover<HTMLButtonElement>();
  const shareTemplateOnClick = useCallback(() => {
    toggle();

    Analytics.sendClickedButtonEvent({
      buttonName: 'shareButton',
      source: 'boardMenuDrawerAboutScreen',
    });
  }, [toggle]);

  const shouldShowShareButton = board?.prefs?.permissionLevel !== 'private';

  const copyCount = board?.templateGallery?.stats.copyCount ?? 0;
  const viewCount = board?.templateGallery?.stats.viewCount ?? 0;
  const storyPageUrl = `/templates/${makeTemplateUrlSlug(
    board?.templateGallery?.category || '',
    board?.name || '',
    board?.shortLink || '',
  )}`;

  const shouldShowViewCopy = isTemplate && copyCount >= 0 && viewCount > 0;

  if (error || loading || !shouldShowViewCopy) {
    return null;
  }

  return (
    <div className={styles.viewCopyComponent}>
      <ViewsAndCopies
        isTemplate={isTemplate}
        showLabels={showLabels}
        useSmallTextStyle={false}
        copyCount={copyCount}
        viewCount={viewCount}
      />
      {shouldShowShareButton && (
        <Button
          className={styles.shareTemplateButton}
          appearance={'link'}
          ref={triggerRef}
          onClick={shareTemplateOnClick}
          iconBefore={<ShareIcon />}
        >
          <FormattedMessage
            id="templates.board_menu_about_this_board.share-template"
            defaultMessage="Share template"
            description="The text used in the share template button"
          />
        </Button>
      )}
      <Popover
        {...popoverProps}
        enableArrowKeyNavigation
        title={
          <FormattedMessage
            id="templates.board_menu_about_this_board.share-template"
            defaultMessage="Share template"
            description="The text used in the share template button"
          />
        }
      >
        <LazyShareTemplatePopover
          storyPageUrl={`${siteDomain}${storyPageUrl}`}
          username={member?.username}
          visibility={board?.prefs?.permissionLevel}
          name={board?.name}
          boardId={idBoard}
          orgName={board?.organization?.displayName}
          enterpriseName={board?.enterprise?.displayName}
          url={board?.url}
          teamId={board?.organization?.id}
        />
      </Popover>
    </div>
  );
};
